/**
 * Longueurs maximales des champs éditables, alignées sur les colonnes MySQL qui
 * les reçoivent (`CG_Addresses`, `CG_Entities`).
 *
 * Sans ces bornes, une valeur trop longue traversait tout le formulaire pour
 * n'échouer qu'à l'INSERT, en fin de soumission, avec une erreur MySQL brute
 * affichée à l'agence (« Data too long for column 'Extension' at row 1 ») :
 * dossier FW-262-LA du 12/08/2026, où l'OCR du justificatif de domicile avait
 * rangé la mention d'hébergement « Chez Madame CHATEIGNIER » dans l'extension
 * du numéro de voie (`varchar(10)`, réservée à « bis » / « ter »). L'agence est
 * restée bloquée deux jours sans savoir quel champ corriger.
 *
 * Miroir front de `packages/backend/src/utils/cgColumnLimits.ts` — les deux
 * doivent bouger ensemble si une migration élargit une colonne.
 */

export interface FieldLengthLimit {
  /** Longueur maximale acceptée (en unités de `unit`). */
  max: number;
  /** Libellé du champ tel qu'il apparaît dans le formulaire. */
  label: string;
  /** Consigne de correction, quand l'erreur vient typiquement d'un mauvais champ. */
  hint?: string;
  /**
   * Mise en forme retirée avant de mesurer, quand le back la retire aussi à
   * l'écriture (SIRET : « 899 182 182 000 27 » est stocké en chiffres nus).
   * Sa présence désactive aussi le `maxLength` de l'input, qui empêcherait de
   * saisir les séparateurs.
   */
  normalize?: (value: string) => string;
  /** Unité comptée, pour le message. Défaut : « caractères ». */
  unit?: string;
}

/** SIRET stocké en chiffres nus (cf. `normalizeSiret` côté back). */
const digitsOnly = (value: string): string => value.replace(/\D/g, '');

const SIRET_LIMIT: FieldLengthLimit = {
  max: 14,
  label: 'SIRET',
  hint: 'Un SIRET compte 14 chiffres (SIREN 9 + NIC 5) : vérifiez-le sur le Kbis ou le certificat de cession.',
  normalize: digitsOnly,
  unit: 'chiffres',
};

/** Clé OCR canonique du champ → borne de la colonne qui le stocke. */
export const FIELD_LENGTH_LIMITS: Record<string, FieldLengthLimit> = {
  // Adresse du titulaire → CG_Addresses
  numero_voie: { max: 10, label: 'Numéro' },
  extension_voie: {
    max: 10,
    label: 'Extension',
    hint: 'Ce champ ne reçoit que l’indice de répétition du numéro (« bis », « ter », « B »…) : une mention « Chez M. / Mme … », un bâtiment ou une résidence se saisissent dans « Complément ».',
  },
  type_voie: { max: 50, label: 'Type de voie' },
  nom_voie: { max: 255, label: 'Nom de voie' },
  lieu_dit: { max: 255, label: 'Lieu-dit' },
  etage_escalier: { max: 50, label: 'Étage / esc. / appt.' },
  complement_adresse: { max: 255, label: 'Complément' },
  boite_postale: { max: 100, label: 'Boîte postale' },
  code_postal: { max: 40, label: 'Code postal' },
  ville: { max: 100, label: 'Ville' },
  pays: { max: 100, label: 'Pays' },
  // Identité du titulaire → CG_Entities
  nom: { max: 255, label: 'Nom' },
  prenom: { max: 255, label: 'Prénom(s)' },
  nom_usage: { max: 255, label: "Nom d'usage" },
  lieu_naissance: { max: 255, label: 'Lieu de naissance' },
  pays_naissance: { max: 255, label: 'Pays de naissance' },
  departement_naissance: { max: 3, label: 'Département de naissance' },
  telephone: { max: 14, label: 'Téléphone' },
  siret: SIRET_LIMIT,
  acheteur_siret: SIRET_LIMIT,
  vendeur_siret: SIRET_LIMIT,
  acheteur_raison_sociale: { max: 255, label: 'Raison sociale' },
};

/** Message inline affiché sous le champ fautif. */
export const formatFieldTooLong = (limit: FieldLengthLimit, actual: number): string =>
  `${limit.label} : ${actual} ${limit.unit ?? 'caractères'} saisis pour ${limit.max} autorisés.${limit.hint ? ` ${limit.hint}` : ''}`;

/**
 * Champs dont la valeur dépasse la colonne cible, sous la forme attendue par
 * `FieldEditList` (`invalidFields` : clé de champ → message inline).
 * Les champs absents, vides ou sans borne connue sont ignorés.
 */
export const findFieldLengthViolations = (
  values: Record<string, string | null | undefined>,
): Record<string, string> => {
  const violations: Record<string, string> = {};
  Object.entries(values).forEach(([fieldKey, rawValue]) => {
    const limit = FIELD_LENGTH_LIMITS[fieldKey];
    if (!limit || typeof rawValue !== 'string') return;
    const value = limit.normalize ? limit.normalize(rawValue) : rawValue.trim();
    if (value.length > limit.max) violations[fieldKey] = formatFieldTooLong(limit, value.length);
  });
  return violations;
};
